import { lazy, Suspense, type ReactNode } from 'react';
import { Routes, Route, Link } from 'react-router-dom';
import { ProtectedRoute } from '@/components/ProtectedRoute';
import { RedirectIfAuthed } from '@/components/RedirectIfAuthed';
import { ExternalRedirect } from '@/components/ExternalRedirect';
import { SiteHeader } from '@/components/SiteHeader';
import { ToastViewport } from '@/components/Toast';
import { DRAFTEDGE, draftedgeSso } from '@/lib/draftedge';
import { useAuthStore } from '@/lib/auth';
import { BrandLogo } from '@/components/BrandLogo';

// Lazy-loaded route bundles — keeps initial payload small for mobile WebView.
const Home = lazy(() => import('./pages/Home'));
const Login = lazy(() => import('./pages/Login'));
const VerifyEmail = lazy(() => import('./pages/VerifyEmail'));
const SsoCallback = lazy(() => import('./pages/SsoCallback'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Account = lazy(() => import('./pages/Account'));
// NOTE: Register / ForgotPassword / Pricing pages are intentionally NOT
// imported — those routes now redirect to draftedge.com (signup + billing
// front door). See the route definitions below + src/lib/draftedge.ts.

// NFL fantasy football draft assistant (the actual product).
const Drafts = lazy(() => import('./pages/Drafts'));
const DraftDetail = lazy(() => import('./pages/DraftDetail'));
const DraftNew = lazy(() => import('./pages/DraftNew'));
const Rankings = lazy(() => import('./pages/Rankings'));
const Cheatsheet = lazy(() => import('./pages/Cheatsheet'));
const Rookies = lazy(() => import('./pages/Rookies'));
const PlayerDetail = lazy(() => import('./pages/PlayerDetail'));

// News list + detail.
const NewsFeed = lazy(() => import('./pages/NewsFeed'));
const NewsArticle = lazy(() => import('./pages/NewsArticle'));

// Full waiver-wire trends list (dashboard card links here).
const WaiverTrends = lazy(() => import('./pages/WaiverTrends'));

// Sleeper tools: cross-league exposure report.
const Exposure = lazy(() => import('./pages/Exposure'));

// League-wide NFL context pages (Sleeper master derived).
const DepthCharts = lazy(() => import('./pages/DepthCharts'));
const Injuries = lazy(() => import('./pages/Injuries'));

function RouteFallback() {
  return (
    <div className="flex min-h-[40vh] items-center justify-center text-ink-muted">
      Loading…
    </div>
  );
}

function NotFound() {
  return (
    <div className="flex flex-col items-center justify-center gap-4 py-24">
      <h1 className="font-display text-3xl">404</h1>
      <p className="text-ink-muted">That page does not exist.</p>
      <Link to="/" className="text-accent underline">
        Back to home
      </Link>
    </div>
  );
}

/**
 * Holds the whole app behind a branded splash until the initial auth check
 * (`bootstrap()` → /v2/auth/me) settles. Without this, a returning user with a
 * stored token gets a flash of the signed-OUT UI (e.g. "Sign in" on the
 * landing) before /me resolves. Anonymous visitors (no stored token) resolve
 * `isBootstrapping` instantly, so they see no delay.
 */
function BootstrapGate({ children }: { children: ReactNode }) {
  const isBootstrapping = useAuthStore((s) => s.isBootstrapping);
  if (isBootstrapping) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4">
        <BrandLogo className="h-8" />
        <span
          className="inline-block h-6 w-6 animate-spin rounded-full border-2 border-ink-muted border-t-accent"
          aria-label="Loading"
        />
      </div>
    );
  }
  return <>{children}</>;
}

export function App() {
  return (
    <div className="min-h-screen bg-surface text-ink font-sans antialiased">
      <BootstrapGate>
        <SiteHeader />
        <Suspense fallback={<RouteFallback />}>
          <Routes>
          <Route path="/" element={<Home />} />

          {/* Pricing / signup / billing live on draftedge.com — redirect out.
              Every in-app "Upgrade"/"Get started"/"Forgot password" link funnels
              through these. Pricing goes via the reverse-SSO bridge: a signed-in
              user lands on their DraftEdge subscription page already logged in;
              a logged-out visitor falls back to the public plans page. */}
          <Route
            path="/pricing"
            element={<ExternalRedirect to={draftedgeSso('subscription', DRAFTEDGE.membership)} />}
          />

          {/* Public news — list + article detail. */}
          <Route path="/news" element={<NewsFeed />} />
          <Route path="/news/:id" element={<NewsArticle />} />
          <Route path="/trending" element={<WaiverTrends />} />
          <Route path="/exposure" element={<Exposure />} />
          <Route path="/depth-charts" element={<DepthCharts />} />
          <Route path="/injuries" element={<Injuries />} />

          {/* Public rankings — shareable cheatsheet, no auth required. */}
          <Route path="/rankings" element={<Rankings />} />
          <Route path="/rookies" element={<Rookies />} />

          {/* Public player profile — deep dive linked from draft glance,
              cheatsheet, and search. No auth gate so URLs are shareable. */}
          <Route path="/players/:id" element={<PlayerDetail />} />

          {/* Public auth routes — bounce already-signed-in users away. */}
          <Route
            path="/login"
            element={
              <RedirectIfAuthed>
                <Login />
              </RedirectIfAuthed>
            }
          />
          <Route
            path="/register"
            element={<ExternalRedirect to={DRAFTEDGE.register} />}
          />
          <Route path="/verify/:token" element={<VerifyEmail />} />
          <Route
            path="/password/forgot"
            element={<ExternalRedirect to={DRAFTEDGE.forgotPassword} />}
          />

          {/* Cross-domain SSO landing — draftedge.com hands off a signed token. */}
          <Route path="/sso" element={<SsoCallback />} />

          {/* Authed-only routes. */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/account"
            element={
              <ProtectedRoute>
                <Account />
              </ProtectedRoute>
            }
          />

          {/* Custom rankings editor — personal cheat sheet (Phase 4.1). */}
          <Route
            path="/cheatsheet"
            element={
              <ProtectedRoute>
                <Cheatsheet />
              </ProtectedRoute>
            }
          />

          {/* Drafts — the core of DraftEdge. */}
          <Route
            path="/drafts"
            element={
              <ProtectedRoute>
                <Drafts />
              </ProtectedRoute>
            }
          />
          <Route
            path="/drafts/new"
            element={
              <ProtectedRoute>
                <DraftNew />
              </ProtectedRoute>
            }
          />
          <Route
            path="/drafts/:id"
            element={
              <ProtectedRoute>
                <DraftDetail />
              </ProtectedRoute>
            }
          />

          <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </BootstrapGate>

      {/* Global toast outlet — sits on top of any route, safe-area aware. */}
      <ToastViewport />
    </div>
  );
}
